/**
 * 事件总线 — 全局消息系统
 * 解耦 UI 层与逻辑层，System 修改状态后通过 EventBus 通知 UI 刷新
 */
export class EventBus {
    private static listeners: Map<string, Array<(...args: any[]) => void>> = new Map();

    /** 订阅事件 */
    static on(event: string, callback: (...args: any[]) => void): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event)!.push(callback);
    }

    /** 取消订阅 */
    static off(event: string, callback: (...args: any[]) => void): void {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            const idx = callbacks.indexOf(callback);
            if (idx >= 0) callbacks.splice(idx, 1);
        }
    }

    /** 发布事件 */
    static emit(event: string, ...args: any[]): void {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(cb => {
                try {
                    cb(...args);
                } catch (e) {
                    console.error(`[EventBus] Error in handler for "${event}":`, e);
                }
            });
        }
    }

    /** 清空所有监听（场景切换时调用） */
    static clear(): void {
        console.warn(`[EventBus] clear() 被调用，清空 ${this.listeners.size} 个事件的所有监听（注意：会移除 EventPopup 等面板的监听）`);
        this.listeners.clear();
    }
}

/** 事件名称常量 */
export const GameEvents = {
    // 状态变更
    STATE_CHANGED: 'stateChanged',
    ATTRIBUTE_CHANGED: 'attributeChanged',
    RESOURCE_CHANGED: 'resourceChanged',
    RANK_CHANGED: 'rankChanged',

    // 游戏流程
    DAY_PASSED: 'dayPassed',
    MONTH_END: 'monthEnd',
    STAGE_PASSED: 'stagePassed',
    GAME_OVER: 'gameOver',

    // 事件系统
    EVENT_TRIGGERED: 'eventTriggered',
    CHOICE_MADE: 'choiceMade',

    // 剧本系统
    SCRIPT_REFRESHED: 'scriptRefreshed',
    // 特殊剧本生命周期信号（批 1 先发，批 2 PlotSystem 接收；docs/特殊剧本实装设计.md §三）
    SPECIAL_CAST: 'specialCast',          // 玩家签下特殊剧本专属位进组
    SPECIAL_DONE: 'specialDone',          // 特殊剧本玩家位杀青
    SPECIAL_AIR_START: 'specialAirStart', // 特殊剧本开播（发在开播当月月末；10-14 茄子令由 airBoom 改名，热爆那名与时点对不上）
    SPECIAL_RATING: 'specialRating',      // 特殊剧本评分揭晓
    // 通告钩子信号（2026-10-24 通告事件批）：**一个信号走天下**——通告类型 × 时点全在 payload 里，
    // 加通告类型（广告/杂志）或加时点都⛔不再加新信号（判定侧一张表一处监听，见 NoticePlotHooks.ts 表头）
    NOTICE_HOOK: 'noticeHook',
    AUDITION_RESULT: 'auditionResult',
    SCRIPT_CONTRACTED: 'scriptContracted',

    // 成就系统
    ACHIEVEMENT_UNLOCKED: 'achievementUnlocked',

    // 小助理
    ASSISTANT_QUEUED: 'assistantQueued',
    ASSISTANT_CONSUMED: 'assistantConsumed',
} as const;

/**
 * **通告钩子信号 payload**（2026-10-24 通告事件批）：通告系统在生命周期时点调 `emitNoticeHook` 发这一份，
 * PlotSystem 拿它查 `NOTICE_PLOT_HOOKS` 表（type × when 命中 → 掷 chance → fire 入队）。
 * 全库只此一个信号、一个 payload 形状——以后广告/杂志系统上线，只在各自时点调发射函数，⛔ 加新信号。
 */
export interface NoticeHookPayload {
    /** 通告类型：drama=拍戏通告；广告/杂志系统上线时扩字面量 */
    type: 'drama';
    /** 时点：done=杀青（我的戏份拍完那一周）／shootWeek=拍摄周行程执行后（非杀青周）／
     *  premiere=开播当月的月初（点「确认行程」那一刻，判据走 `dramaTimelineOf().premiere` 唯一出口）。
     *  加时点＝加发射行+扩此枚举 */
    when: 'done' | 'shootWeek' | 'premiere';
    /** 通告主体 id（drama=剧本 scriptId）——插值 {script}/{role} 与「同事件不同剧」查重都读它 */
    refId: string;
    /** drama：玩家饰演的角色位（{role} 插值用） */
    roleIndex?: number;
}

/** 通告钩子发射口（唯一）：payload 形状在这里收口，别在发射点手拼 EventBus.emit */
export function emitNoticeHook(p: NoticeHookPayload): void {
    EventBus.emit(GameEvents.NOTICE_HOOK, p);
}